/**
 * Ajout d'une recette au journal — la **séquence en quatre temps** des specs §6.5.
 *
 *   1. choix de la quantité consommée (portions ou poids) — panneau `quantity` ;
 *   2. écran d'ajustement des ingrédients, chaque quantité éditable **pour cette
 *      occurrence uniquement** — panneau `adjust` ;
 *   3. la recette enregistrée n'est **jamais** modifiée ;
 *   4. l'entrée apparaît au journal comme un bloc groupé, repliable et
 *      ré-éditable — voir `MealCard` et `RecipeBlockForm`.
 *
 * 🔴 **Point 3, concrètement.** Rien ici n'appelle `PATCH /api/recipes/:id`, et
 * le protocole d'ajout ne l'accepterait pas : `RecipeEntryInput` ne transporte
 * que `recipeId`, une quantité, et des quantités d'ingrédients qui n'atteignent
 * que `log_entry_ingredient` (§4.5). La recette ne peut pas bouger, parce qu'il
 * n'y a aucun chemin par lequel elle le pourrait.
 *
 * ⚠️ **Deux panneaux, une seule feuille** (specs §6.3, « sans quitter l'écran »).
 * L'ajustement remplace la quantité dans la même feuille, avec un retour
 * explicite — empiler une seconde feuille modale serait une impasse gestuelle
 * sur iPhone.
 *
 * ⚠️ Aucun total n'est envoyé au serveur (§5.1). Ce qui est affiché ici est un
 * aperçu (`lib/recipePreview.ts`) ; le serveur relit `food.*_100` pour chaque
 * ligne et fige le résultat.
 */

import { useState } from 'react';

import type { LogEntryInput, MacroSet, RecipeDTO } from '../../../../shared/types';
import { Button, Hint, NumberField } from '../../components/ui/primitives';
import { Card } from '../../components/nourri';
import { MacroDot } from '../../components/ui/MacroMeter';
import type { EntryPreview } from '../../hooks/useDay';
import { toNumber } from '../../lib/formFields';
import { formatMacro, MACRO_ROWS } from '../../lib/macros';
import {
  composeRecipeLabel,
  composeRecipePortionLabel,
  macrosOfLine,
  previewLinesFor,
  sumMacros,
  type PreviewLine,
} from '../../lib/recipePreview';

/** Bandeau de total, partagé par les deux panneaux. */
export function MacroPreview({ macros, title }: { macros: MacroSet; title: string }) {
  return (
    <Card>
      <div className="n-eyebrow" style={{ marginBottom: 8 }}>
        {title}
      </div>
      <div className="n-preview">
        {MACRO_ROWS.map((row) => (
          <div key={row.key}>
            <div className="n-preview__label">
              <MacroDot macro={row.key} />
              {row.short}
            </div>
            <div className="n-preview__value tnum">{formatMacro(macros[row.key], row.unit)}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * Liste de quantités d'ingrédients, éditable — le cœur du §6.5-2.
 *
 * Réutilisée telle quelle par la **ré-édition** d'un bloc déjà journalisé
 * (`RecipeBlockForm`) : c'est exactement le même geste, à un moment différent.
 */
export function IngredientQuantityRows({
  lines,
  fields,
  onFieldChange,
  baseUnitOf,
}: {
  lines: readonly { key: number; label: string; unit: string }[];
  fields: Record<number, string>;
  onFieldChange: (key: number, value: string) => void;
  baseUnitOf: (key: number) => MacroSet;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
      {lines.map((line) => {
        const macros = baseUnitOf(line.key);
        return (
          <div
            key={line.key}
            className="n-ing-row"
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="n-ing-row__name n-ellipsis">{line.label}</div>
              <div className="n-pick__meta tnum">
                {formatMacro(macros.protein, 'g')} P · {formatMacro(macros.carbs, 'g')} G ·{' '}
                {formatMacro(macros.fat, 'g')} L · {formatMacro(macros.kcal, 'kcal')} kcal
              </div>
            </div>
            <div style={{ width: '6.5rem', flexShrink: 0 }}>
              <NumberField
                label={line.unit}
                value={fields[line.key] ?? ''}
                onChange={(value) => {
                  onFieldChange(line.key, value);
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Convertit les lignes proposées en champs de saisie (chaînes). */
function fieldsOfLines(lines: readonly PreviewLine[]): Record<number, string> {
  const fields: Record<number, string> = {};
  for (const line of lines) {
    fields[line.ingredientId] = String(line.quantity).replace('.', ',');
  }
  return fields;
}

export function RecipeEntryStep({
  recipe,
  mealName,
  onBack,
  onValidate,
}: {
  recipe: RecipeDTO;
  mealName: string;
  onBack?: () => void;
  onValidate: (input: LogEntryInput, preview: EntryPreview) => void;
}) {
  const isPortions = recipe.yieldType === 'portions';

  const [quantity, setQuantity] = useState(isPortions ? '1' : '200');
  /** `null` tant qu'on est au temps 1 ; renseigné au passage au temps 2. */
  const [fields, setFields] = useState<Record<number, string> | null>(null);

  const typed = Math.max(0, toNumber(quantity));

  /*
   * Lignes proposées pour la quantité saisie — le point de départ de l'écran
   * d'ajustement, avant toute correction. Recalculées tant qu'on est au temps 1
   * pour que le total suive la quantité ; figées dès qu'on passe au temps 2,
   * sinon corriger un ingrédient réinitialiserait les autres.
   */
  const proposed = previewLinesFor(recipe, typed);

  const adjusted: PreviewLine[] =
    fields === null
      ? proposed
      : proposed.flatMap((line) => {
          const value = Math.max(0, toNumber(fields[line.ingredientId] ?? ''));
          if (value <= 0) return [];
          return [
            {
              ...line,
              quantity: value,
              macros: macrosOfLine(line.macros, line.quantity, value),
            },
          ];
        });

  const totals = sumMacros(adjusted.map((line) => line.macros));
  const canSubmit = typed > 0 && adjusted.length > 0;

  const submit = () => {
    if (!canSubmit) return;

    const preview: EntryPreview = {
      label: composeRecipeLabel(recipe.name),
      quantity: typed,
      unit: isPortions ? 'portion' : 'g',
      portionLabel: composeRecipePortionLabel(recipe, typed),
      macros: totals,
      ingredients: adjusted.map((line) => ({
        label: line.label,
        quantity: line.quantity,
        unit: line.unit,
        macros: line.macros,
      })),
    };

    onValidate(
      {
        type: 'recipe',
        recipeId: recipe.id,
        quantity: typed,
        /*
         * ⚠️ **Les quantités, jamais les macros.** Le serveur relit `food.*_100`
         * pour chaque ligne et calcule tout (§5.1) ; le protocole n'accepte
         * d'ailleurs aucun champ de total. Les lignes retirées ne sont pas
         * envoyées à zéro mais **omises de la liste explicite** : on transmet
         * l'état voulu de chaque ligne, et le serveur retire ce qui est à zéro.
         */
        ingredients: proposed.map((line) => ({
          ingredientId: line.ingredientId,
          quantity:
            fields === null
              ? line.quantity
              : Math.max(0, toNumber(fields[line.ingredientId] ?? '')),
        })),
      },
      preview,
    );
  };

  /* --- Temps 1 : quantité consommée ------------------------------------ */

  if (fields === null) {
    const perUnit = isPortions ? recipe.perPortion : recipe.per100;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
          {onBack && (
            <Button variant="quiet" onClick={onBack} style={{ padding: '0 0.75rem' }}>
              ‹ Retour
            </Button>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{recipe.name}</p>
            <p className="n-hint">
              Ma recette · {String(recipe.ingredients.length)} ingrédient(s) → {mealName}
            </p>
          </div>
        </div>

        {recipe.tags.length > 0 && (
          <span className="n-pick__meta">{recipe.tags.join(' · ')}</span>
        )}

        <NumberField
          label={
            isPortions
              ? `Portions consommées (la recette en fait ${formatMacro(recipe.yieldValue, 'g')})`
              : `Poids consommé (g) — la recette pèse ${formatMacro(recipe.yieldValue, 'g')} g`
          }
          value={quantity}
          onChange={setQuantity}
        />

        {perUnit !== null && (
          <Hint>
            {isPortions ? 'Une portion' : '100 g'} : {formatMacro(perUnit.protein, 'g')} P ·{' '}
            {formatMacro(perUnit.carbs, 'g')} G · {formatMacro(perUnit.fat, 'g')} L ·{' '}
            {formatMacro(perUnit.kcal, 'kcal')} kcal.
          </Hint>
        )}

        <MacroPreview macros={totals} title="Aperçu — recalculé par le serveur à la validation" />

        <Button
          disabled={typed <= 0}
          onClick={() => {
            setFields(fieldsOfLines(proposed));
          }}
        >
          Ajuster les ingrédients ›
        </Button>

        <Hint>
          L’étape suivante permet de corriger chaque quantité **pour cette fois seulement** — la
          recette enregistrée n’est jamais modifiée.
        </Hint>
      </div>
    );
  }

  /* --- Temps 2 : ajustement des ingrédients ---------------------------- */

  const macrosByKey = new Map(adjusted.map((line) => [line.ingredientId, line.macros]));
  const rows = proposed.map((line) => ({
    key: line.ingredientId,
    label: line.label,
    unit: line.unit,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        <Button
          variant="quiet"
          onClick={() => {
            setFields(null);
          }}
          style={{ padding: '0 0.75rem' }}
        >
          ‹ Retour
        </Button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{recipe.name}</p>
          <p className="n-hint">
            {isPortions
              ? `${formatMacro(typed, 'g')} portion(s) sur ${formatMacro(recipe.yieldValue, 'g')}`
              : `${formatMacro(typed, 'g')} g sur ${formatMacro(recipe.yieldValue, 'g')} g`}{' '}
            → {mealName}
          </p>
        </div>
      </div>

      <span className="n-field__label">
        Ingrédients — quantités de cette fois
      </span>

      <IngredientQuantityRows
        lines={rows}
        fields={fields}
        onFieldChange={(key, value) => {
          setFields((current) => ({ ...(current ?? {}), [key]: value }));
        }}
        baseUnitOf={(key) => macrosByKey.get(key) ?? { protein: 0, carbs: 0, fat: 0, kcal: 0 }}
      />

      <Hint>
        Mettre une quantité à 0 retire l’ingrédient de cette entrée. La recette enregistrée, elle,
        ne bouge pas.
      </Hint>

      <MacroPreview macros={totals} title="Total de l’entrée — recalculé par le serveur" />

      <Button disabled={!canSubmit} onClick={submit}>
        {`Ajouter à ${mealName}`}
      </Button>
    </div>
  );
}

/**
 * Ré-édition d'un bloc **déjà journalisé** (specs §6.5-d).
 *
 * ⚠️ Ne connaît pas la recette source, et n'en a pas besoin : elle a pu être
 * modifiée ou supprimée depuis. Les lignes du bloc sont autonomes (§4.5), et
 * c'est la seule chose qu'on corrige ici.
 *
 * ⚠️ Les macros affichées sont mises à l'échelle **depuis les valeurs figées**,
 * par règle de trois — le client ne connaît pas `food.*_100` pour ces lignes, et
 * n'a pas à le connaître (§5.1). Le serveur, lui, relira la fiche si elle existe
 * encore ; c'est sa réponse qui fait foi.
 */
export function RecipeBlockForm({
  entry,
  onSubmit,
}: {
  entry: {
    label: string;
    ingredients: { id: number; label: string; quantity: number; unit: string; macros: MacroSet }[];
  };
  onSubmit: (lines: { id: number; quantity: number }[]) => void;
}) {
  const [fields, setFields] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    for (const line of entry.ingredients) {
      initial[line.id] = String(line.quantity).replace('.', ',');
    }
    return initial;
  });

  const current = entry.ingredients.map((line) => {
    const value = Math.max(0, toNumber(fields[line.id] ?? ''));
    return {
      id: line.id,
      quantity: value,
      macros: value <= 0 ? { protein: 0, carbs: 0, fat: 0, kcal: 0 } : macrosOfLine(line.macros, line.quantity, value),
    };
  });

  const macrosByKey = new Map(current.map((line) => [line.id, line.macros]));
  const totals = sumMacros(current.map((line) => line.macros));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <p style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{entry.label}</p>

      <IngredientQuantityRows
        lines={entry.ingredients.map((line) => ({
          key: line.id,
          label: line.label,
          unit: line.unit,
        }))}
        fields={fields}
        onFieldChange={(key, value) => {
          setFields((state) => ({ ...state, [key]: value }));
        }}
        baseUnitOf={(key) => macrosByKey.get(key) ?? { protein: 0, carbs: 0, fat: 0, kcal: 0 }}
      />

      <Hint>
        Mettre une quantité à 0 retire définitivement la ligne de cette entrée : elle ne pourra plus
        y être remise. Pour repartir de la recette complète, supprimer l’entrée et la rajouter.
      </Hint>

      <MacroPreview macros={totals} title="Nouveau total de l’entrée" />

      <Button
        disabled={current.every((line) => line.quantity <= 0)}
        onClick={() => {
          onSubmit(current.map((line) => ({ id: line.id, quantity: line.quantity })));
        }}
      >
        Enregistrer la correction
      </Button>

      <Hint>
        Seule cette entrée change. La recette enregistrée et les autres journées ne sont pas
        touchées.
      </Hint>
    </div>
  );
}
